import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from './store/store'
import { checkAuth } from './store/slices/authSlice'

// Layouts
import PublicLayout from './layouts/PublicLayout'
import PrivateLayout from './layouts/PrivateLayout'
import AdminLayout from './layouts/AdminLayout'

// Public Pages
import HomePage from './pages/public/HomePage'
import AuthPage from './pages/auth/AuthPage'
import LoginPage from './pages/auth/LoginPage'
import PricingPage from './pages/public/PricingPage'
import PlanDetailsPage from './pages/public/PlanDetailsPage'
import AboutPage from './pages/public/AboutPage'
import ContactPage from './pages/public/ContactPage'
import BlogsPage from './pages/public/BlogsPage'
import BlogDetailPage from './pages/public/BlogDetailPage'
import CodeOfConductPage from './pages/public/CodeOfConductPage'
import RiskDisclosurePage from './pages/public/RiskDisclosurePage'
import MITCPage from './pages/public/MITCPage'
import InvestorCharterPage from './pages/public/InvestorCharterPage'
import DetailsOfComplaintPage from './pages/public/DetailsOfComplaintPage'

// Private Pages
import DashboardPage from './pages/private/DashboardPage'
import RecommendationsPage from './pages/private/RecommendationsPage'
import RecommendationDetailsPage from './pages/private/RecommendationDetailsPage'
import ProfilePage from './pages/private/ProfilePage'
import KYCPage from './pages/private/KYCPage'
import ESignPage from './pages/private/ESignPage'
import SubscriptionPage from './pages/private/SubscriptionPage'
import NotificationsPage from './pages/private/NotificationsPage'
import CheckoutPage from './pages/private/CheckoutPage'

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage'
import AdminDashboard from './pages/admin/AdminDashboard'
import ClientRecords from './pages/admin/ClientRecords'
import PlansManagement from './pages/admin/PlansManagement'
import PlanDetailPage from './pages/admin/PlanDetailPage'
import TransactionsPage from './pages/admin/TransactionsPage'
import PurchasesPage from './pages/admin/PurchasesPage'
import DocumentsPage from './pages/admin/DocumentsPage'
import AuditTrailPage from './pages/admin/AuditTrailPage'
import GrievancesPage from './pages/admin/GrievancesPage'
import ContentManagement from './pages/admin/ContentManagement'
import CreateRecommendation from './pages/admin/CreateRecommendation'
import BlogManagementPage from './pages/admin/BlogManagementPage'
import BlogEditorPage from './pages/admin/BlogEditorPage'
import KYCManagement from './pages/admin/KYCManagement'
import SubscriptionManagement from './pages/admin/SubscriptionManagement'
import NotificationManagement from './pages/admin/NotificationManagement'
import AdminSettings from './pages/admin/AdminSettings'
import CouponManagement from './pages/admin/CouponManagement'

// Components
import ProtectedRoute from './components/common/ProtectedRoute'
import LoadingSpinner from './components/common/LoadingSpinner'
import ScrollToTop from './components/common/ScrollToTop'
import { resolvePostAuthRoute } from './utils/accessFlow'

function AuthRouteRedirect() {
  const { user } = useSelector((state: RootState) => state.auth)
  return <Navigate to={resolvePostAuthRoute({ user })} replace />
}

const needsProfileCompletion = (user: RootState['auth']['user']) =>
  Boolean(user && (!user.phone || !user.pan_number || !user.date_of_birth))

function App() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { isLoading, isAuthenticated, user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    console.log('[App] Route changed', location.pathname)
  }, [location.pathname])

  useEffect(() => {
    console.log('[App] Auth state changed', { isLoading, isAuthenticated, user: user?.email || 'guest' })
  }, [isLoading, isAuthenticated, user])

  useEffect(() => {
    dispatch(checkAuth() as any)
    document.documentElement.classList.add('dark')
    console.log('[App] Initializing auth check')
  }, [dispatch])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-950">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="pricing/:slug" element={<PlanDetailsPage />} />
        <Route path="plans/:planRef" element={<PlanDetailsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="academy" element={<BlogsPage />} />
        <Route path="blogs" element={<BlogsPage />} />
        <Route path="blogs/:slug" element={<BlogDetailPage />} />
        <Route path="code-of-conduct" element={<CodeOfConductPage />} />
        <Route path="risk-disclosure" element={<RiskDisclosurePage />} />
        <Route path="mitc" element={<MITCPage />} />
        <Route path="investor-charter" element={<InvestorCharterPage />} />
        <Route path="details-of-complaint" element={<DetailsOfComplaintPage />} />
      </Route>

      {/* Auth Routes — unified smart auth page */}
      <Route path="/login" element={isAuthenticated && !needsProfileCompletion(user) ? <AuthRouteRedirect /> : <AuthPage />} />
      <Route path="/register" element={isAuthenticated && !needsProfileCompletion(user) ? <AuthRouteRedirect /> : <AuthPage />} />
      <Route path="/auth" element={isAuthenticated && !needsProfileCompletion(user) ? <AuthRouteRedirect /> : <AuthPage />} />
      {/* Legacy routes kept for direct links */}
      <Route path="/login-legacy" element={<LoginPage />} />
      <Route path="/register-legacy" element={isAuthenticated && !needsProfileCompletion(user) ? <AuthRouteRedirect /> : <AuthPage />} />

        {/* Private Routes.
          Dashboard/Profile/KYC/eSign/Subscription/Checkout are accessible without a plan.
          Plan-gated content keeps using <ProtectedRoute requirePlan>. */}
      <Route path="/dashboard" element={<ProtectedRoute requirePlan={false}><PrivateLayout /></ProtectedRoute>}>
        <Route index element={<ProtectedRoute requirePlan={false}><DashboardPage /></ProtectedRoute>} />
        <Route path="recommendations" element={<ProtectedRoute requirePlan><RecommendationsPage /></ProtectedRoute>} />
        <Route path="recommendations/:slug" element={<ProtectedRoute requirePlan><RecommendationDetailsPage /></ProtectedRoute>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="kyc" element={<KYCPage />} />
        <Route path="esign" element={<ESignPage />} />
        <Route path="notifications" element={<ProtectedRoute requirePlan={false}><NotificationsPage /></ProtectedRoute>} />
        <Route path="checkout" element={<ProtectedRoute requirePlan={false}><CheckoutPage /></ProtectedRoute>} />
      </Route>

      {/* Verified users may choose/purchase a plan without already owning one. */}
      <Route path="/dashboard/subscription" element={<ProtectedRoute requirePlan={false}><PrivateLayout /></ProtectedRoute>}>
        <Route index element={<SubscriptionPage />} />
      </Route>

      {/* Admin Login Route */}
      <Route path="/admin/login" element={isAuthenticated && user?.is_admin ? <Navigate to="/admin" replace /> : <AdminLoginPage />} />

      {/* Admin Protected Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        {/* New compliance desk pages */}
        <Route path="clients" element={<ClientRecords />} />
        <Route path="plans" element={<PlansManagement />} />
        <Route path="plans/:slug" element={<PlanDetailPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="purchases" element={<PurchasesPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="audit-trail" element={<AuditTrailPage />} />
        <Route path="complaints" element={<GrievancesPage />} />
        {/* Existing pages */}
        <Route path="content" element={<ContentManagement />} />
        <Route path="content/create-recommendation" element={<CreateRecommendation />} />
        <Route path="content/edit-recommendation/:id" element={<CreateRecommendation />} />
        <Route path="blogs" element={<BlogManagementPage />} />
        <Route path="blogs/new" element={<BlogEditorPage />} />
        <Route path="blogs/:id/edit" element={<BlogEditorPage />} />
        <Route path="kyc" element={<KYCManagement />} />
        <Route path="subscriptions" element={<SubscriptionManagement />} />
        <Route path="notifications" element={<NotificationManagement />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="coupons" element={<CouponManagement />} />
        {/* Legacy redirects */}
        <Route path="users" element={<Navigate to="/admin/clients" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default App
